import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { usePatchEstimateSplit } from "../../../../views/Orders/hooks/usePatchEstimateSplit";
import { OrderPayableBy, OrderPayableTo } from "../../../../views/Orders/enums";
import { isInvalidPaymentRouteCombination } from "../utils/paymentRouteValidation";
import { extractApiErrorMessage } from "../../utils/apiErrorUtils";

export interface SplitPaymentRouteEditorProps {
  estimateNo: string;
  splitId: string;
  payableBy?: OrderPayableBy;
  payableTo?: OrderPayableTo;
  transactionType?: string;
  isExpired: boolean;
  onSaved: (estimate: unknown) => void;
}

export const SplitPaymentRouteEditor: React.FC<SplitPaymentRouteEditorProps> = ({
  estimateNo,
  splitId,
  payableBy: payableByProp,
  payableTo: payableToProp,
  transactionType,
  isExpired,
  onSaved,
}) => {
  const toast = useToast();
  const [payableBy, setPayableBy] = useState<OrderPayableBy | "">(
    payableByProp || "",
  );
  const [payableTo, setPayableTo] = useState<OrderPayableTo | "">(
    payableToProp || "",
  );

  useEffect(() => {
    setPayableBy(payableByProp || "");
    setPayableTo(payableToProp || "");
  }, [payableByProp, payableToProp]);

  const { mutate: patchEstimateSplit, isLoading: isUpdating } =
    usePatchEstimateSplit();

  const isPaymentRouteInvalid = isInvalidPaymentRouteCombination(
    payableBy,
    payableTo,
  );

  const handleSave = () => {
    if (!transactionType) {
      toast({
        title: "Transaction type required",
        description: "Select transaction type first",
        status: "warning",
        duration: 2000,
        position: "top",
      });
      return;
    }
    if (isPaymentRouteInvalid) return;
    if (!payableBy || !payableTo) {
      toast({
        title: "Selection missing",
        description: "Please select both Payable By and Payable To",
        status: "warning",
        duration: 2000,
        position: "top",
      });
      return;
    }

    patchEstimateSplit(
      {
        estimateNo,
        splitId,
        payload: { payableBy, payableTo },
      },
      {
        onSuccess: (estimate) => {
          toast({
            title: "Payment Route updated",
            status: "success",
            duration: 2000,
            position: "top",
          });
          onSaved(estimate);
        },
        onError: (err) => {
          toast({
            title: "Failed to update Payment Route",
            description: extractApiErrorMessage(err, String(err)),
            status: "error",
            duration: 3000,
            position: "top",
          });
        },
      },
    );
  };

  const canEdit = Boolean(transactionType) && !isExpired;

  const editIconSx = {
    fontSize: 16,
    color: canEdit ? "#805AD5" : "#9CA3AF",
  };

  if (!canEdit) {
    return (
      <EditOutlinedIcon sx={editIconSx} aria-hidden />
    );
  }

  return (
    <Popover placement="right" trigger="click" isLazy closeOnBlur>
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label="Edit payment route"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          p={0.5}
          lineHeight={0}
          cursor="pointer"
          bg="transparent"
          border="none"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <EditOutlinedIcon sx={editIconSx} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={1600}
          width="180px"
          boxShadow="lg"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody>
            <VStack spacing={2} align="stretch" mt={1}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable By
                </Text>
                <Select
                  size="xs"
                  value={payableBy}
                  onChange={(e) =>
                    setPayableBy(e.target.value as OrderPayableBy)
                  }
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  {Object.values(OrderPayableBy).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable To
                </Text>
                <Select
                  size="xs"
                  value={payableTo}
                  onChange={(e) =>
                    setPayableTo(e.target.value as OrderPayableTo)
                  }
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  {Object.values(OrderPayableTo).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              {isPaymentRouteInvalid && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  fontWeight="600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  lineHeight="short"
                >
                  The selected case is invalid. Please choose a valid case.
                </Text>
              )}
              <HStack spacing={2} pt={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  flex="1"
                  onClick={() => {
                    setPayableBy(payableByProp || "");
                    setPayableTo(payableToProp || "");
                  }}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  color="#2D3748"
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  flex="1"
                  onClick={handleSave}
                  isLoading={isUpdating}
                  isDisabled={isExpired || isPaymentRouteInvalid}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
